<script setup lang="ts">
import {
  computed,
  reactive,
  ref,
  watch,
} from 'vue';
import {
  useRoute,
} from 'vue-router';

import type { ITableState } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VRouterTabs from '@components/tabs/VRouterTabs/VRouterTabs.vue';
import CreativePreview from '@modules/affiliate-program-creatives/components/CreativePreview/CreativePreview.vue';
import { useAffiliateProgramCreativePreview } from '@modules/affiliate-program-creatives/composables';
import {
  AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
} from '@modules/affiliate-program-creatives/constants';
import { useAffiliateProgramCreativesListQuery } from '@modules/affiliate-program-creatives/queries';
import {
  AFFILIATE_PROGRAM_CREATIVES_BULK_ADD_ROUTE_NAME,
  AFFILIATE_PROGRAM_CREATIVES_CREATE_ROUTE_NAME,
  AFFILIATE_PROGRAM_CREATIVES_EDIT_ROUTE_NAME,
} from '@modules/affiliate-program-creatives/submodules/edit/constants';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import { isString } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import {
  useAffiliateProgramCreativesListMultipleActions,
  useAffiliateProgramCreativesListPage,
} from './composables';

const route = useRoute();

const isDisabled = ref(false);
const creativeTablesState = reactive<Record<string, ITableState>>({
  link: { page: 1 },
  jpeg: { page: 1 },
  png: { page: 1 },
  gif: { page: 1 },
  bannerflow_gif: { page: 1 },
  iframe: { page: 1 },
});
const type = computed<string>(() => (isString(route.params.type) ? route.params.type : ''));

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliateProgramCreativesListQuery, type, isDisabled);

function onTabClick(): void {
  isDisabled.value = true;
}

watchImmediate(tableState, (newState) => {
  creativeTablesState[type.value] = newState;
});

watch(type, (newValue) => {
  if (newValue) {
    updateTableState(creativeTablesState[newValue]);
    isDisabled.value = false;
  }
});

const {
  filterOptions,
  tableTabs,
  columnOptions,
} = useAffiliateProgramCreativesListPage(type);

const {
  multiSelectOptions,
  onMultiselectAction,
} = useAffiliateProgramCreativesListMultipleActions(
  tableState,
  type,
);

const {
  previewCreative,
  onPreview,
  onPreviewHide,
} = useAffiliateProgramCreativePreview();
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateProgramCreativesListPage'" type="table">
    <VTable
      :data="data"
      :state="creativeTablesState[type]"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'HEADER')"
      :filter-props="filterOptions"
      :columns-options="columnOptions"
      :multiselect="multiSelectOptions"
      @state-update="updateTableState"
      @multiselect-action="onMultiselectAction"
      @refresh="refreshTableData"
    >
      <template #tabs>
        <VRouterTabs
          :items="tableTabs"
          replace
          @click="onTabClick"
        />
      </template>
      <template
        v-if="$hasPermission(AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS.CREATE)"
        #footerButtons
      >
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'SUBMIT')"
          :to="{
            name: AFFILIATE_PROGRAM_CREATIVES_CREATE_ROUTE_NAME,
            state: {
              format: type,
            },
          }"
        />
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'CREATE_BULK')"
          :to="{
            name: AFFILIATE_PROGRAM_CREATIVES_BULK_ADD_ROUTE_NAME,
            state: {
              format: type,
            },
          }"
        />
      </template>
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="listItem.format !== 'link'"
          icon="pi-eye"
          @click="onPreview(listItem)"
        />
        <VIconButton
          v-if="$hasPermission(AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS.EDIT)"
          icon="pi-file-edit"
          :to="{
            name: AFFILIATE_PROGRAM_CREATIVES_EDIT_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
      </template>
    </VTable>

    <CreativePreview
      :creative="previewCreative"
      @hide="onPreviewHide"
    />
  </VCard>
</template>
